import { useCallback, useEffect, useMemo, useState } from 'react'
import { BarChart3, Loader2 } from 'lucide-react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ReportFilterBar } from '@/components/reports/ReportFilterBar'
import {
  exportDailySalesReport,
  fetchDailySalesReport,
  formatReportMoney,
  resolveReportPeriodBounds,
} from '@/lib/api/reports'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { useBranch } from '@/contexts/BranchContext'
import { useReportPanelFilters } from '@/contexts/ReportPreferencesContext'

export function DailySalesReportPanel() {
  const { activeBranchId } = useBranch()
  const { period, setPeriod, compare, setCompare, customFrom, setCustomFrom, customTo, setCustomTo } =
    useReportPanelFilters()
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const bounds = useMemo(() => {
    try {
      return resolveReportPeriodBounds(period, { from: customFrom, to: customTo })
    } catch {
      return null
    }
  }, [period, customFrom, customTo])

  const reportOptions = useMemo(
    () => ({
      period,
      from: period === 'custom' ? customFrom : bounds?.from,
      to: period === 'custom' ? customTo : bounds?.to,
      compare,
    }),
    [period, customFrom, customTo, bounds, compare],
  )

  const loadReport = useCallback(async () => {
    if (period === 'custom' && (!customFrom || !customTo)) {
      setReport(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')

    try {
      const data = await fetchDailySalesReport(reportOptions)
      setReport(data)
    } catch (err) {
      setReport(null)
      setError(apiErrorMessage(err, 'Unable to load daily sales report.'))
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, reportOptions])

  useEffect(() => {
    loadReport()
  }, [loadReport, activeBranchId])

  const handleExport = async (format) => {
    setExporting(true)
    setError('')
    try {
      await exportDailySalesReport(format, reportOptions)
    } catch (err) {
      setError(apiErrorMessage(err, `Unable to export ${format.toUpperCase()} report.`))
    } finally {
      setExporting(false)
    }
  }

  const chartData = useMemo(() => {
    if (!report?.hourly_trend) return []
    return report.hourly_trend
      .filter((row) => Number(row.gross_total) > 0 || row.sale_count > 0)
      .map((row) => ({
        label: row.label,
        gross: Number(row.gross_total),
        sales: row.sale_count,
      }))
  }, [report])

  const currency = report?.branch_currency ?? 'AED'
  const hqConversion = report?.hq_conversion

  return (
    <section className="card-surface p-6 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <BarChart3 className="h-5 w-5" aria-hidden />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Daily sales summary</h2>
            <p className="text-sm text-muted-foreground">
              Completed sales by day with hourly revenue trend for the selected period.
            </p>
          </div>
        </div>
        <ReportFilterBar
          period={period}
          compare={compare}
          customFrom={customFrom}
          customTo={customTo}
          onPeriodChange={setPeriod}
          onCompareChange={setCompare}
          onCustomFromChange={setCustomFrom}
          onCustomToChange={setCustomTo}
          onExportCsv={() => handleExport('csv')}
          onExportPdf={() => handleExport('pdf')}
          exporting={exporting}
        />
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : report ? (
        <>
          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard label="Sales" value={String(report.summary.sale_count)} />
            <SummaryCard
              label="Gross revenue"
              value={`${currency} ${formatReportMoney(report.summary.gross_total)}`}
            />
            <SummaryCard
              label="Average ticket"
              value={`${currency} ${formatReportMoney(report.summary.average_ticket)}`}
            />
            <SummaryCard
              label="Discounts"
              value={`${currency} ${formatReportMoney(report.summary.discount_total)}`}
            />
          </div>

          <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label="VAT"
              value={`${currency} ${formatReportMoney(report.summary.tax_total)}`}
            />
            <SummaryCard
              label="Tips"
              value={`${currency} ${formatReportMoney(report.summary.tip_total)}`}
            />
            <SummaryCard label="Voids" value={String(report.summary.void_count)} />
            <SummaryCard
              label="Refunds"
              value={String(report.summary.refund_count)}
            />
          </div>

          {report.compare_summary ? (
            <p className="mb-4 text-sm text-muted-foreground">
              Compare period: {report.compare_summary.sale_count} sales · {currency}{' '}
              {formatReportMoney(report.compare_summary.gross_total)} gross
            </p>
          ) : null}

          {hqConversion?.available ? (
            <p className="mb-4 rounded-xl border border-border/70 bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
              HQ equivalent ({hqConversion.hq_currency}):{' '}
              <span className="font-medium text-foreground">
                {hqConversion.hq_currency} {formatReportMoney(hqConversion.converted_amount)}
              </span>
              {' · '}
              {hqConversion.fx_rate_label}
              {' · '}
              {hqConversion.note}
            </p>
          ) : hqConversion && !hqConversion.available ? (
            <p className="mb-4 text-sm text-muted-foreground">{hqConversion.note}</p>
          ) : null}

          {chartData.length > 0 ? (
            <div className="mb-6 rounded-2xl border border-border/70 bg-muted/10 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Hourly trend
                {report.hourly_mode === 'hour_of_day'
                  ? ' — totals by hour across selected days'
                  : ''}
              </p>
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={176}>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={48} />
                    <Tooltip
                      formatter={(value) => [`${currency} ${formatReportMoney(value)}`, 'Gross']}
                      labelFormatter={(label) => `Hour ${label}`}
                    />
                    <Bar dataKey="gross" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          <div className="overflow-x-auto rounded-2xl border border-border/70">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Sales</th>
                  <th className="px-4 py-3 font-medium">Gross</th>
                  <th className="px-4 py-3 font-medium">Discount</th>
                  <th className="px-4 py-3 font-medium">Tax</th>
                  <th className="px-4 py-3 font-medium">Avg ticket</th>
                </tr>
              </thead>
              <tbody>
                {report.by_day.every((row) => row.sale_count === 0) ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                      No completed sales in this period.
                    </td>
                  </tr>
                ) : (
                  report.by_day.map((row) => (
                    <tr key={row.date} className="border-t border-border/60">
                      <td className="px-4 py-3 font-medium">{row.date}</td>
                      <td className="px-4 py-3">{row.sale_count}</td>
                      <td className="px-4 py-3">{currency} {formatReportMoney(row.gross_total)}</td>
                      <td className="px-4 py-3">{currency} {formatReportMoney(row.discount_total)}</td>
                      <td className="px-4 py-3">{currency} {formatReportMoney(row.tax_total)}</td>
                      <td className="px-4 py-3">{currency} {formatReportMoney(row.average_ticket)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function SummaryCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-muted/20 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold">{value}</p>
    </div>
  )
}
